import React from 'react';
import { Document, Page as RPDFPage, View, Text, StyleSheet } from '@react-pdf/renderer';
import type {
  AdmitCardApplicant, AdmitCardCycle, IdNamePair,
} from '../../../types/admissions';
import {
  OfficialHeader, NumberedList, Signatures,
  pdfColors, pdfFontFamily, pdfFontSize, pdfLetter, pdfRadius,
  fmtDate, fmtClassName, fmtMarks,
  type SchoolBranding, type DocumentMeta, type PhotoDataUrls,
} from '../../../utils/pdf';
import { PhotoSlot } from './sharedFieldRendering';
import { testSittingOf } from '../../../utils/admissionRecords';
import { photoFilenameOf } from '../../../utils/personPhoto';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { assessmentNoun, pathwayFor } from '../../../utils/admissionAssessment';

interface Props {
  app: AdmitCardApplicant;
  cycle: AdmitCardCycle;
  branding: SchoolBranding;
  meta: DocumentMeta;
  /** Applicants' photos (fetchPhotoDataUrls); a card without one shows the affix box. */
  photos?: PhotoDataUrls;
}

/** Gap between the sheet edge and the card frame — also the cut margin. */
const SHEET_MARGIN = 18;

/** Instruction text beyond this many characters steps down a font size so
 *  a long list still fits the card's fixed height. */
const LONG_INSTRUCTIONS_CHARS = 420;

const styles = StyleSheet.create({
  sheet: {
    padding: SHEET_MARGIN,
    fontFamily: pdfFontFamily.sans,
    fontSize: pdfFontSize.sm,
    color: pdfColors.textPrimary,
    backgroundColor: pdfColors.surface,
  },

  // ── Card frame ────────────────────────────────────────────────────────
  card: {
    flex: 1,
    borderWidth: 1.2,
    borderColor: pdfColors.textPrimary,
    borderStyle: 'solid',
    borderRadius: pdfRadius.lg,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 10,
  },

  // ── Candidate ─────────────────────────────────────────────────────────
  main: { flexDirection: 'row', marginBottom: 10 },
  info: { flex: 1, paddingRight: 14 },
  rollRow: { flexDirection: 'row', alignItems: 'flex-end', marginBottom: 8 },
  rollLabel: {
    fontSize: pdfFontSize.xxs,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openStrong,
    color: pdfColors.textMuted,
  },
  roll: {
    fontFamily: pdfFontFamily.mono,
    fontSize: 19,
    fontWeight: 700,
    color: pdfColors.textPrimary,
    marginTop: 1,
  },
  classChip: {
    marginLeft: 'auto',
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: pdfRadius.md,
    backgroundColor: pdfColors.brand,
  },
  classChipText: { fontSize: pdfFontSize.md, fontWeight: 700, color: pdfColors.textOnDark },
  name: { fontSize: pdfFontSize.xl + 1, fontWeight: 700, letterSpacing: pdfLetter.tight },
  parentage: { fontSize: pdfFontSize.sm, color: pdfColors.textSecondary, marginTop: 1, marginBottom: 6 },
  facts: { flexDirection: 'row', flexWrap: 'wrap' },
  fact: {
    width: '50%',
    paddingVertical: 3,
    borderTopWidth: 0.5,
    borderTopColor: pdfColors.divider,
    borderTopStyle: 'solid',
  },
  factLabel: {
    fontSize: pdfFontSize.xxs,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel,
    color: pdfColors.textMuted,
  },
  factValue: { fontSize: pdfFontSize.sm, fontWeight: 500, marginTop: 1 },
  factMono: { fontFamily: pdfFontFamily.mono, fontSize: pdfFontSize.xs + 0.5 },

  // ── Test schedule strip ───────────────────────────────────────────────
  strip: {
    flexDirection: 'row',
    borderWidth: 0.6,
    borderColor: pdfColors.divider,
    borderStyle: 'solid',
    borderRadius: pdfRadius.md,
    backgroundColor: pdfColors.surfaceSoft,
    marginBottom: 8,
  },
  stripCell: { flex: 1, paddingHorizontal: 10, paddingVertical: 6 },
  stripCellDivider: { borderLeftWidth: 0.5, borderLeftColor: pdfColors.divider, borderLeftStyle: 'solid' },
  stripValue: { fontSize: pdfFontSize.md, fontWeight: 600, marginTop: 2 },
  stripTba: { fontSize: pdfFontSize.sm, fontStyle: 'italic', color: pdfColors.textMuted, marginTop: 2 },
  papers: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 },
  paper: { fontSize: pdfFontSize.xs, color: pdfColors.textSecondary, marginRight: 10 },
  paperMarks: { fontWeight: 600, color: pdfColors.textPrimary },

  // ── Instructions + sign-off ───────────────────────────────────────────
  instructions: { flex: 1, overflow: 'hidden' },
  foot: {
    flexDirection: 'row',
    marginTop: 6,
    paddingTop: 5,
    borderTopWidth: 0.5,
    borderTopColor: pdfColors.divider,
    borderTopStyle: 'solid',
  },
  footText: { fontSize: pdfFontSize.xxs, color: pdfColors.textMuted },
  footRight: { marginLeft: 'auto' },

  // ── Bulk sheet: two cards on A4 with a cut guide ──────────────────────
  half: { flex: 1 },
  cut: { flexDirection: 'row', alignItems: 'center', marginVertical: SHEET_MARGIN / 2 },
  cutLine: { flex: 1, borderTopWidth: 0.6, borderTopColor: pdfColors.dividerStrong, borderTopStyle: 'dashed' },
  cutText: {
    marginHorizontal: 8,
    fontSize: pdfFontSize.xxs,
    color: pdfColors.textFaint,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel,
  },
});

function nameOf(ref: string | IdNamePair | null | undefined): string {
  return ref && typeof ref === 'object' ? ref.name || '' : '';
}

const StripCell: React.FC<{ label: string; value?: string | null; first?: boolean }> = ({ label, value, first }) => (
  <View style={first ? styles.stripCell : [styles.stripCell, styles.stripCellDivider]}>
    <Text style={styles.rollLabel}>{label}</Text>
    {value ? <Text style={styles.stripValue}>{value}</Text> : <Text style={styles.stripTba}>To be announced</Text>}
  </View>
);

/** The card itself — sized by its parent, so the same card fills an A5
 *  page on its own or half of an A4 sheet in a bulk print run. */
export const AdmitCard: React.FC<Props> = ({ app, cycle, branding, photos }) => {
  const card = app.admitCard;
  const className = fmtClassName(nameOf(app.classId));
  const parentage = app.gender === 'female' ? 'D/o' : app.gender === 'male' ? 'S/o' : 'Child of';
  const sitting = testSittingOf(cycle, app);
  const pathway = pathwayFor(cycle, app.classId);
  const papers = pathway.stages?.testEnabled ? pathway.testConfig?.papers ?? [] : [];
  // "Test date", or "Observation date" for a class assessed without a test.
  const noun = assessmentNoun(pathway);
  const instructions = cycle.admitCardInstructions ?? [];

  return (
    <View style={styles.card}>
      <OfficialHeader branding={branding} title="Admit Card" details={[{ value: cycle.name }]} compact />

      {/* Candidate */}
      <View style={styles.main}>
        <View style={styles.info}>
          <View style={styles.rollRow}>
            <View>
              <Text style={styles.rollLabel}>Roll number</Text>
              <Text style={styles.roll}>{card?.rollNumber || '—'}</Text>
            </View>
            {className ? (
              <View style={styles.classChip}><Text style={styles.classChipText}>{className}</Text></View>
            ) : null}
          </View>
          <Text style={styles.name}>{app.name}</Text>
          {app.fatherName ? <Text style={styles.parentage}>{parentage} {app.fatherName}</Text> : null}
          <View style={styles.facts}>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>Application no.</Text>
              <Text style={[styles.factValue, styles.factMono]}>{app.applicationNumber}</Text>
            </View>
          </View>
        </View>
        <PhotoSlot width={78} height={96} src={photos?.[photoFilenameOf(app) ?? '']} />
      </View>

      {/* Test schedule */}
      <View style={styles.strip}>
        <StripCell first label={`${noun} date`} value={sitting.testDate ? fmtDate(sitting.testDate) : null} />
        <StripCell label="Reporting time" value={sitting.testTime ? dateTimeUtils.formatClockTime(sitting.testTime) : null} />
        <StripCell label="Venue" value={sitting.testVenue} />
      </View>
      {papers.length > 0 && (
        <View style={styles.papers}>
          <Text style={[styles.rollLabel, { marginRight: 8 }]}>Papers</Text>
          {papers.map((p) => (
            <Text key={p.key} style={styles.paper}>
              {p.subject} <Text style={styles.paperMarks}>{fmtMarks(p.maxMarks)}</Text>
            </Text>
          ))}
          {pathway.testConfig?.maxMarks ? (
            <Text style={styles.paper}>
              Total <Text style={styles.paperMarks}>{fmtMarks(pathway.testConfig.maxMarks)}</Text>
            </Text>
          ) : null}
        </View>
      )}

      {/* Instructions — takes the remaining height of the card */}
      <View style={styles.instructions}>
        {instructions.length > 0 && (
          <>
            <Text style={[styles.rollLabel, { marginBottom: 4 }]}>Instructions</Text>
            <NumberedList
              items={instructions}
              size={instructions.join('').length > LONG_INSTRUCTIONS_CHARS ? pdfFontSize.xxs : pdfFontSize.xs}
              columns={2}
            />
          </>
        )}
      </View>

      <Signatures slots={['Candidate', 'Parent / guardian', 'Controller of examinations']} inkSpace={20} />

      <View style={styles.foot}>
        <Text style={styles.footText}>Issued {fmtDate(card?.issuedAt)}</Text>
        <Text style={[styles.footText, styles.footRight]}>
          Bring this card to the {noun.toLowerCase()}. Not valid without a photograph.
        </Text>
      </View>
    </View>
  );
};

/** A5-landscape page holding one card. */
export const AdmitCardPage: React.FC<Props> = (props) => (
  <RPDFPage size="A5" orientation="landscape" style={styles.sheet} wrap={false}>
    <AdmitCard {...props} />
  </RPDFPage>
);

/** A4-portrait sheet holding two cards with a dashed cut guide between —
 *  the print-room layout for bulk runs (half the paper of one-per-page). */
export const AdmitCardSheet: React.FC<Omit<Props, 'app'> & { apps: AdmitCardApplicant[] }> = ({ apps, ...rest }) => (
  <RPDFPage size="A4" style={styles.sheet} wrap={false}>
    <View style={styles.half}>
      <AdmitCard app={apps[0]} {...rest} />
    </View>
    {apps[1] ? (
      <View style={styles.cut}>
        <View style={styles.cutLine} />
        <Text style={styles.cutText}>Cut along this line</Text>
        <View style={styles.cutLine} />
      </View>
    ) : (
      <View style={styles.cut} />
    )}
    <View style={styles.half}>
      {apps[1] && <AdmitCard app={apps[1]} {...rest} />}
    </View>
  </RPDFPage>
);

/** AdmitCardDocument — one candidate's admit card on an A5 page. */
const AdmitCardDocument: React.FC<Props> = (props) => (
  <Document title={props.meta.title} author={props.branding.name}>
    <AdmitCardPage {...props} />
  </Document>
);

export default AdmitCardDocument;
